import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { UserPermission, ConfigurationModulePermission, AccessLevel } from '../models/user-permission.model';
import { HttpClient } from '@angular/common/http';
import { filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})

export class UserManagementService {
  
  private userPermissionsSubject = new BehaviorSubject<UserPermission[] | null>(null);
  userPermissions$ = this.userPermissionsSubject.asObservable();
  
  constructor(private http: HttpClient) 
  { 
    this.loadUserPermissions();
  }

  private loadUserPermissions(): void {
    this.http
        .get<UserPermission[]>('/assets/UserPermissions.json')
        .subscribe(data => {
          this.userPermissionsSubject.next(data);
        });
  }

  // Get all users as an observable
  getUsers(): Observable<UserPermission[]> {
    return this.userPermissions$.pipe(
      filter((users): users is UserPermission[] => users !== null)
    );
  }

  // Add a new user
  addUser(email: string, permissions: ConfigurationModulePermission[]): void {
    const currentUsers = this.userPermissionsSubject.value || [];
    const existingUser = currentUsers.find(u => u.email === email);
    
    if (existingUser) {
      throw new Error('User already exists');
    }

    const newUser: UserPermission = {
      email,
      userPermissions: permissions
    };

    const updatedUsers = [...currentUsers, newUser];
    this.userPermissionsSubject.next(updatedUsers);
  }

  // Update user permissions
  updateUser(email: string, permissions: ConfigurationModulePermission[]): void {
    const currentUsers = this.userPermissionsSubject.value || [];
    const userIndex = currentUsers.findIndex(u => u.email === email);
    
    if (userIndex === -1) {
      throw new Error('User does not exist');
    }

    const updatedUsers = [...currentUsers];
    updatedUsers[userIndex] = {
      ...updatedUsers[userIndex],
      userPermissions: permissions
    };
    this.userPermissionsSubject.next(updatedUsers);
  }

  // Remove a user
  removeUser(email: string): void {
    const currentUsers = this.userPermissionsSubject.value || [];
    const updatedUsers = currentUsers.filter(u => u.email !== email);
    this.userPermissionsSubject.next(updatedUsers);
  }

  // Get specific user
  getUserPermissions(email: string): Observable<UserPermission | undefined> {
    const normalizedEmail = (email ?? '').trim().toLowerCase();
    return this.userPermissions$.pipe(
      filter((users): users is UserPermission[] => users !== null),
      map(users => users.find(u => (u.email ?? '').trim().toLowerCase() === normalizedEmail))
    );
  }

  // Get configuration IDs user has access to
  getUserConfigurationIds(email: string): Observable<number[]> {
    return this.getUserPermissions(email).pipe(
      map(user => {
        if (!user) return [];
        return [...new Set(user.userPermissions.map(p => p.configurationID))];
      })
    );
  }

  // Check if user has access to specific configuration
  hasConfigurationAccess(email: string, configId: number): Observable<boolean> {
    return this.getUserPermissions(email).pipe(
      map(user => {
        if (!user) return false;
        return user.userPermissions.some(p => p.configurationID === configId);
      })
    );
  }

  // Get modules user has access to for a specific configuration
  getConfigurationModules(email: string, configId: number): Observable<ConfigurationModulePermission[]> {
    return this.getUserPermissions(email).pipe(
      map(user => {
        if (!user) return [];
        return user.userPermissions.filter(p => p.configurationID === configId);
      })
    );
  }

  // Check if user has write access to a specific module
  hasWriteAccess(email: string, configId: number, moduleId: number): Observable<boolean> {
    return this.getUserPermissions(email).pipe(
      map(user => {
        if (!user) return false;
        
        const permission = user.userPermissions.find(
          p => p.configurationID === configId && p.moduleID === moduleId
        );
        
        return permission?.accessLevel === AccessLevel.ReadWrite;
      })
    );
  }

  // Check if user has at least read access to a specific module
  hasReadAccess(email: string, configId: number, moduleId: number): Observable<boolean> {
    return this.getUserPermissions(email).pipe(
      map(user => {
        if (!user) return false;
        
        const permission = user.userPermissions.find(
          p => p.configurationID === configId && p.moduleID === moduleId
        );
        
        return permission?.accessLevel === AccessLevel.Read || permission?.accessLevel === AccessLevel.ReadWrite;
      })
    );
  }

  // Get access level for a specific module
  getAccessLevel(email: string, configId: number, moduleId: number): Observable<AccessLevel | null> {
    return this.getUserPermissions(email).pipe(
      map(user => {
        if (!user) return null;
        
        const permission = user.userPermissions.find(
          p => p.configurationID === configId && p.moduleID === moduleId
        );
        
        return permission?.accessLevel || null;
      })
    );
  }
}